/**
 * useHydration hook - Track IndexedDB hydration state
 *
 * Provides reactive hydration state for stores loaded from IndexedDB
 */

'use client';

import { useSelector } from '@legendapp/state/react';
import { hydrationState$, isStoreHydrated, waitForHydration } from '../stores/plugins/sync-plugin-indexeddb';

/**
 * Hydration hook return type
 */
export interface UseHydrationReturn {
    /** Whether all stores have been hydrated from IndexedDB */
    isHydrated: boolean;
    /** Whether stores are currently loading */
    isLoading: boolean;
    /** Set of store names that have been hydrated */
    hydratedStores: Set<string>;
    /** Check if a specific store is hydrated */
    isStoreHydrated: (storeName: string) => boolean;
    /** Wait for all stores to be hydrated */
    waitForHydration: () => Promise<void>;
}

/**
 * Hook for tracking IndexedDB hydration state
 *
 * Use this hook to ensure stores are loaded before rendering UI
 * that depends on persisted state
 *
 * @example
 * ```typescript
 * const { isHydrated, isLoading } = useHydration();
 *
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (!isHydrated) {
 *   return <LoadingPlaceholder />;
 * }
 *
 * return <CartContent />;
 * ```
 */
export const useHydration = (): UseHydrationReturn => {
    const isHydrated = useSelector(() => hydrationState$.isHydrated.get());
    const isLoading = useSelector(() => hydrationState$.isLoading.get());
    const hydratedStores = useSelector(() => hydrationState$.hydratedStores.get());

    return {
        isHydrated,
        isLoading,
        hydratedStores,
        isStoreHydrated,
        waitForHydration,
    };
};

export default useHydration;
